import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './Faq.scss';
import FaqAccordion, { adaptFAQ, Chevron } from './FaqAccordion';

const API_URL = process.env.REACT_APP_API_URL;

// Shown when the backend endpoint isn't set up yet or returns an error.
// Replace these with real content once /getFAQ is live.
const PLACEHOLDER_SECTIONS = [
  {
    title: 'Download Troubleshooting',
    items: [
      { question: 'How do I upload a file?', steps: [{ text: 'Content coming soon.', img: null }] },
      { question: 'What file types are supported?', steps: [{ text: 'Content coming soon.', img: null }] },
    ],
  },
  {
    title: 'Upload Troubleshooting',
    items: [
      { question: 'The upload failed — what do I do?', steps: [{ text: 'Content coming soon.', img: null }] },
      { question: 'Who do I contact for support?', steps: [{ text: 'Content coming soon.', img: null }] },
    ],
  },
];

// Static contact details — edit these to match your real support contact.
const CONTACT_INFO = {
  email: 'avsupport@avionica.com',
  phone: '+1 (786) 544-1141', 
};

const FAQ = ({ companyName }) => {
  const [sections, setSections] = useState([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [usingPlaceholder, setUsingPlaceholder] = useState(false);

  useEffect(() => {
    const fetchFAQ = async () => {
      try {
        const url = `${API_URL}/getFAQ${companyName ? `?company=${encodeURIComponent(companyName)}` : ''}`;
        const response = await fetch(url);
        const text = await response.text();

        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const data = JSON.parse(text);
        const parsedSections = adaptFAQ(data);
        if (parsedSections.length === 0) throw new Error('FAQ response had no usable sections');

        setSections(parsedSections);
      } catch (err) {
        console.warn('FAQ endpoint not available, showing placeholder:', err.message);
        setSections(PLACEHOLDER_SECTIONS);
        setUsingPlaceholder(true);
      } finally {
        setLoading(false);
      }
    };

    fetchFAQ();
  }, [companyName]);

  if (loading) {
    return (
      <div className="faq-container">
        <p className="faq-status">Loading FAQ…</p>
      </div>
    );
  }

  return (
    <div className="faq-container">
      <h2 className="faq-title">Frequently Asked Questions</h2>
      {usingPlaceholder && (
        <p className="faq-placeholder-notice">Content coming soon — backend not yet connected.</p>
      )}

      <div className="faq-scroll">
        <FaqAccordion sections={sections} />

        {/* Contact Us — static section */}
        <div className="faq-section-wrapper">
          <button
            className={`faq-section-header ${contactOpen ? 'open' : ''}`}
            onClick={() => setContactOpen(prev => !prev)}
            aria-expanded={contactOpen}
          >
            <span>Contact Us</span>
            <Chevron />
          </button>

          {contactOpen && (
            <div className="faq-section-body">
              <div className="faq-contact">
                <p className="faq-contact-note">
                  Before reaching out, please check the answers above — most questions are
                  covered in the FAQ, and it's the fastest way to get unblocked.
                </p>
                <div className="faq-contact-box">
                  <p>
                    <strong>Email:</strong>{' '}
                    <a href={`mailto:${CONTACT_INFO.email}`}>{CONTACT_INFO.email}</a>
                  </p>
                  <p>
                    <strong>Phone:</strong>{' '}
                    <a href={`tel:${CONTACT_INFO.phone.replace(/[^+\d]/g, '')}`}>
                      {CONTACT_INFO.phone}
                    </a>
                  </p>
                  <p><strong>Hours:</strong> {CONTACT_INFO.hours}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

FAQ.propTypes = {
  companyName: PropTypes.string,
};

export default FAQ;
